import { defineStore } from 'pinia'
import { computed, markRaw, ref, shallowRef, watch } from 'vue'

import type {
  ComfyWorkflow,
  PendingWarnings
} from '@/platform/workflow/management/stores/comfyWorkflow'
import { api } from '@/scripts/api'
import { ChangeTracker } from '@/scripts/changeTracker'
import { defaultGraphJSON } from '@/scripts/defaultGraph'
import { useToastStore } from '@/platform/updates/common/toastStore'
import type {
  ComfyWorkflowJSON,
  NodeId
} from '@/platform/workflow/validation/schemas/workflowSchema'
import type { NordyUser } from '../interfaces'
import type {
  WorkflowCreateDto,
  WorkflowUpdateDto
} from '../interfaces/workflow.interface'
import { isObjectIdRegex } from '../utils/isObjectIdRegex'
import { nordyApi } from '../api'
import type { ResultNordyQueue } from './nordyQueueStore'
import { useNordyUserStore } from './nordyUserStore'
import { app } from '@/scripts/app'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import type {
  LGraph,
  LGraphNode,
  Subgraph
} from '@/lib/litegraph/src/litegraph'
import {
  createNodeExecutionId,
  createNodeLocatorId,
  parseNodeExecutionId,
  parseNodeLocatorId
} from '@/types/nodeIdentification'
import type { NodeExecutionId, NodeLocatorId } from '@/types/nodeIdentification'
import { isSubgraph } from '@/utils/typeGuardUtil'
import type { Raw } from 'vue'
import { useNordyWorkflowApiPromptStore } from './nordyWorkflowApiPromptStore'

export class NordyComfyWorkflow {
  /**
   * The change tracker for the workflow. Non-reactive raw object.
   */
  changeTracker: ChangeTracker | null = null
  //!nordy - upstream pendingWarnings support for missing model/node warnings
  pendingWarnings: PendingWarnings | null = null
  /**
   * Whether the workflow has been modified comparing to the initial state.
   */
  private _isModified: boolean = false

  workflowName: string
  content: string | null = null
  originalContent: string | null = null
  isLoading: boolean = false

  //!nordy
  userId: string | null = null
  workflowId: string
  currentUserId: string | null = null
  kind: 'workflow' | 'image' | 'temporary' = 'workflow'
  originalCover: string | null = null

  lastOutputAsset: ResultNordyQueue | null = null
  readonly uid: string
  prompt: string | undefined = undefined

  constructor(options: {
    workflowId: string
    workflowName: string
    currentUserId: string | null
    kind: 'workflow' | 'image' | 'temporary'
    userId: string | null
    originalCover: string | null
    prompt: string | undefined
  }) {
    this.workflowId = options.workflowId
    this.workflowName = options.workflowName
    this.currentUserId = options.currentUserId
    this.kind = options.kind
    this.originalCover = options.originalCover
    this.userId = options.userId
    this.prompt = options.prompt
    this.uid = this.generateTimestampWithRandom()
  }

  private generateTimestampWithRandom(): string {
    const timestamp = Date.now().toString()
    const random = Math.floor(Math.random() * 100000000)
      .toString()
      .padStart(8, '0')
    return `${timestamp}${random}`
  }

  get path() {
    return this.sessionWorkflowId
  }

  get isTemporary() {
    return !isObjectIdRegex.test(this.workflowId)
  }

  get isPersisted() {
    return !this.isTemporary
  }

  //!nordy - 동적으로 현재 유저 확인 (currentUserId 생성 시점 불일치 방지)
  get isMyWorkflow() {
    const nordyUserStore = useNordyUserStore()
    const currentUserId = nordyUserStore?.nordyUser?._id ?? null
    return this.userId !== null && this.userId === currentUserId
  }

  get key() {
    return this.sessionWorkflowId
  }

  get activeState(): ComfyWorkflowJSON | null {
    return this.changeTracker?.activeState ?? null
  }

  get initialState(): ComfyWorkflowJSON | null {
    return this.changeTracker?.initialState ?? null
  }

  get isLoaded(): boolean {
    return this.changeTracker !== null
  }

  get isModified(): boolean {
    return this._isModified
  }

  set isModified(value: boolean) {
    this._isModified = value
  }

  /**
   * example key
   * workflow:{"clientId":"x4z5ihsqpm9-1742157508341","workflowId":"Untitled(1)","workflowName":"Untitled(1)","kind":"temporary","userId":null}
   */
  get sessionWorkflowId() {
    const jsonString = JSON.stringify({
      clientId: api.clientId,
      workflowId: this.workflowId,
      workflowName: this.workflowName,
      kind: this.kind,
      userId: this.userId
    })

    return `workflow:${jsonString}`
  }

  async load() {
    if (this.isLoaded) {
      return this as NordyLoadedComfyWorkflow
    }

    const getWorkflowJson =
      this.content ??
      sessionStorage.getItem(this.sessionWorkflowId) ??
      defaultGraphJSON

    this.originalContent = getWorkflowJson
    this.content = getWorkflowJson

    try {
      sessionStorage.setItem(this.sessionWorkflowId, this.originalContent)
    } catch {
      console.warn('[NordyWorkflow] sessionStorage quota exceeded during load')
    }

    const parsedWorkflowJson = JSON.parse(this.originalContent)

    this.changeTracker = markRaw(
      new ChangeTracker(this as unknown as ComfyWorkflow, parsedWorkflowJson)
    )

    this.changeTracker.store()

    return this as NordyLoadedComfyWorkflow
  }

  unload(): void {
    // unload workflow
    this.changeTracker = null
    this.content = null
    this.originalContent = null
    this.isLoading = false
    sessionStorage.removeItem(this.sessionWorkflowId)
  }

  async update(dto: WorkflowUpdateDto) {
    const ret = await nordyApi.updateWorkflow(this.workflowId, dto)
    return ret
  }

  private async updateUrlWithWorkflowId(workflowId: string) {
    const searchParams = new URLSearchParams(window.location.search)
    // 기존 파라미터(flow, image, job) 정리 후 flow로 통일
    searchParams.delete('image')
    searchParams.delete('job')
    searchParams.set('flow', workflowId)
    const newUrl = `${window.location.pathname}?${searchParams.toString()}`
    window.history.replaceState({}, '', newUrl)
  }

  async save(saveName?: string, isNewWorkflow: boolean = false) {
    this.content = JSON.stringify(this.activeState)
    const workflowApiPromptStore = useNordyWorkflowApiPromptStore()
    if (this.activeState === null) {
      throw new Error('activeState is null')
    }

    const prompt = await app.graphToPrompt()

    // 노드가 다 연결된 그래프를 따로 저장해야 함.
    let isWorkflowApiPromptError = false
    workflowApiPromptStore.setFakeJobRunning(true)
    // 가짜 잡 만들어서 실행 하기 (beforeQueued 콜백 포함)
    try {
      await app.queuePrompt(1, 1)
    } catch (error) {
      console.error('save error:', error)
      isWorkflowApiPromptError = true
    }
    workflowApiPromptStore.setFakeJobRunning(false)
    const workflowApiPrompt = workflowApiPromptStore.getWorkflowApiPrompt()

    // 이후에 다시 비워주기
    workflowApiPromptStore.setWorkflowApiPrompt(null)

    const dto: WorkflowCreateDto = {
      name: saveName ?? this.workflowName,
      prompt: JSON.stringify(prompt),
      workflowApiPrompt: isWorkflowApiPromptError ? null : workflowApiPrompt,
      editedAt: new Date()
    }

    // 기존에 저장된 커버가 없을경우에만 생성한 이미지를 썸네일, 커버로 설정
    if (this.originalCover === null) {
      dto.cover = this.lastOutputAsset?.isImage
        ? this.lastOutputAsset.assetId
        : undefined
      dto.thumbnails = this.lastOutputAsset?.isImage
        ? [this.lastOutputAsset.assetId]
        : []
    }

    try {
      const resultWorkflow = isNewWorkflow
        ? await nordyApi.saveWorkflow(dto)
        : this.isMyWorkflow
          ? await this.update(dto)
          : await nordyApi.saveWorkflow(dto)

      sessionStorage.removeItem(this.sessionWorkflowId)
      const resultUserId =
        (resultWorkflow.user as NordyUser)._id ??
        (resultWorkflow.user as unknown as string)

      this.isModified = false
      this.userId = resultUserId
      this.workflowId = resultWorkflow._id
      this.workflowName = resultWorkflow.name
      this.originalCover = resultWorkflow.cover
      this.kind = 'workflow'
      this.changeTracker?.reset()

      await this.updateUrlWithWorkflowId(resultWorkflow._id)
      try {
        sessionStorage.setItem(this.sessionWorkflowId, this.content)
      } catch {
        console.warn(
          '[NordyWorkflow] sessionStorage quota exceeded during save'
        )
      }

      return resultWorkflow
    } catch (error) {
      console.error('save error:', error)
      throw error
    }
  }
}

export interface NordyLoadedComfyWorkflow extends NordyComfyWorkflow {
  isLoaded: true
  originalContent: string
  content: string
  changeTracker: ChangeTracker
  initialState: ComfyWorkflowJSON
  activeState: ComfyWorkflowJSON
}

export interface NordyWorkflowStore {
  activeWorkflow: NordyLoadedComfyWorkflow | null
  isActive: (workflow: NordyComfyWorkflow) => boolean
  openWorkflows: NordyLoadedComfyWorkflow[]
  openedWorkflowIndexShift: (shift: number) => NordyLoadedComfyWorkflow | null
  openWorkflow: (
    workflow: NordyComfyWorkflow
  ) => Promise<NordyLoadedComfyWorkflow>

  workflows: NordyComfyWorkflow[]
  saveWorkflow: (workflow: NordyComfyWorkflow) => Promise<void>

  isSubgraphActive: boolean
  activeSubgraph: Subgraph | undefined

  updateActiveGraph: () => void
  executionIdToCurrentId: (id: string) => string | undefined
  nodeIdToNodeLocatorId: (nodeId: NodeId, subgraph?: Subgraph) => NodeLocatorId
  nodeToNodeLocatorId: (node: LGraphNode) => NodeLocatorId
  nodeExecutionIdToNodeLocatorId: (
    nodeExecutionId: NodeExecutionId | string
  ) => NodeLocatorId | null
  nodeLocatorIdToNodeId: (locatorId: NodeLocatorId | string) => NodeId | null
  nodeLocatorIdToNodeExecutionId: (
    locatorId: NodeLocatorId | string,
    targetSubgraph?: Subgraph
  ) => NodeExecutionId | null
}

export const useNordyWorkflowStore = defineStore('nordyWorkflow', () => {
  const toastStore = useToastStore()
  const nordyUserStore = useNordyUserStore()

  const detachWorkflow = (workflow: NordyComfyWorkflow) => {
    const oldSessionWorkflowId = workflow.sessionWorkflowId
    delete workflowLookup.value[oldSessionWorkflowId]
    const index = openWorkflowPaths.value.indexOf(oldSessionWorkflowId)
    if (index !== -1) {
      openWorkflowPaths.value = openWorkflowPaths.value.filter(
        (sessionWorkflowId) => sessionWorkflowId !== oldSessionWorkflowId
      )
    }
    return { index, oldSessionWorkflowId }
  }

  const attachWorkflow = (
    workflow: NordyComfyWorkflow,
    detachInfo: { index: number; oldSessionWorkflowId: string }
  ) => {
    const newSessionWorkflowId = workflow.sessionWorkflowId
    workflowLookup.value[newSessionWorkflowId] = workflow
    if (detachInfo.index !== -1) {
      openWorkflowPaths.value.splice(detachInfo.index, 0, newSessionWorkflowId)
    }
  }

  const activeWorkflow = ref<NordyLoadedComfyWorkflow | null>(null)
  const isActive = (workflow: NordyComfyWorkflow) =>
    activeWorkflow.value?.sessionWorkflowId === workflow.sessionWorkflowId

  const workflowLookup = ref<Record<string, NordyComfyWorkflow>>({})
  const workflows = computed<NordyComfyWorkflow[]>(() =>
    Object.values(workflowLookup.value)
  )

  const getWorkflowByPath = (
    sessionWorkflowId: string
  ): NordyComfyWorkflow | null => {
    return workflowLookup.value[sessionWorkflowId] ?? null
  }

  const openWorkflowPaths = ref<string[]>([])
  const openWorkflowPathSet = computed(() => new Set(openWorkflowPaths.value))
  const openWorkflows = computed(() =>
    openWorkflowPaths.value.map(
      (sessionWorkflowId) => workflowLookup.value[sessionWorkflowId]
    )
  )

  const reorderWorkflows = (from: number, to: number) => {
    const movedTab = openWorkflowPaths.value[from]
    openWorkflowPaths.value.splice(from, 1)
    openWorkflowPaths.value.splice(to, 0, movedTab)
  }

  const isOpen = (workflow: NordyComfyWorkflow) =>
    openWorkflowPathSet.value.has(workflow.sessionWorkflowId)

  /**
   * Add paths to the list of open workflow paths without loading the files
   * or changing the active workflow.
   * lodash _.union replaced with native Set-based deduplication.
   */
  const openWorkflowsInBackground = (paths: {
    left?: string[]
    right?: string[]
  }) => {
    const { left = [], right = [] } = paths
    if (!left.length && !right.length) return

    const isValidPath = (
      sessionWorkflowId: unknown
    ): sessionWorkflowId is keyof typeof workflowLookup.value =>
      typeof sessionWorkflowId === 'string' &&
      sessionWorkflowId in workflowLookup.value

    const merged = [...left, ...openWorkflowPaths.value, ...right]
    openWorkflowPaths.value = Array.from(new Set(merged)).filter(isValidPath)
  }

  const openWorkflow = async (
    workflow: NordyComfyWorkflow
  ): Promise<NordyLoadedComfyWorkflow> => {
    const sessionWorkflowId = workflow.sessionWorkflowId

    if (isActive(workflow)) return workflow as NordyLoadedComfyWorkflow

    if (!openWorkflowPaths.value.includes(sessionWorkflowId)) {
      openWorkflowPaths.value.push(sessionWorkflowId)
    }
    workflowLookup.value[sessionWorkflowId] = workflow
    const loadedWorkflow = await workflow.load()

    activeWorkflow.value = loadedWorkflow
    return loadedWorkflow
  }

  const nodeIdToNodeLocatorId = (
    nodeId: NodeId,
    subgraph?: Subgraph
  ): NodeLocatorId => {
    const targetSubgraph = subgraph ?? activeSubgraph.value
    if (!targetSubgraph) {
      return String(nodeId)
    }

    return createNodeLocatorId(targetSubgraph.id, nodeId)
  }

  const nodeToNodeLocatorId = (node: LGraphNode): NodeLocatorId => {
    if (isSubgraph(node.graph))
      return createNodeLocatorId(node.graph.id, node.id)
    return String(node.id)
  }

  const nodeLocatorIdToNodeId = (
    locatorId: NodeLocatorId | string
  ): NodeId | null => {
    const parsed = parseNodeLocatorId(locatorId)
    return parsed?.localNodeId ?? null
  }

  /**
   * Convert an execution ID to a NodeLocatorId
   * @param nodeExecutionId The execution node ID (e.g., "123:456:789")
   * @returns The NodeLocatorId or null if conversion fails
   */
  const nodeExecutionIdToNodeLocatorId = (
    nodeExecutionId: NodeExecutionId | string
  ): NodeLocatorId | null => {
    // Handle simple node IDs (root graph - no colons)
    if (!nodeExecutionId.includes(':')) {
      return nodeExecutionId
    }

    const parts = parseNodeExecutionId(nodeExecutionId)
    if (!parts || parts.length === 0) return null

    const nodeId = parts[parts.length - 1]
    const subgraphNodeIds = parts.slice(0, -1)

    if (subgraphNodeIds.length === 0) {
      return String(nodeId)
    }

    try {
      const subgraphs = getSubgraphsFromInstanceIds(
        app.rootGraph,
        subgraphNodeIds.map((id) => String(id))
      )
      const immediateSubgraph = subgraphs[subgraphs.length - 1]
      return createNodeLocatorId(immediateSubgraph.id, nodeId)
    } catch {
      return null
    }
  }

  const getUnconflictedWorkflowName = () => {
    let workflowName = 'Untitled(1)'
    const untitledNumbers = openWorkflows.value
      .map((w) => {
        const match = w.workflowName.match(/^Untitled\((\d+)\)$/)
        return match ? parseInt(match[1]) : 0
      })
      .filter((n) => n > 0)
      .sort((a, b) => a - b)

    let nextNumber = 1
    for (const num of untitledNumbers) {
      if (num > nextNumber) {
        break
      }
      nextNumber = num + 1
    }

    if (
      openWorkflows.value.some((w) => w.workflowName === 'Untitled') ||
      untitledNumbers.length > 0
    ) {
      workflowName = `Untitled(${nextNumber})`
    }

    return workflowName
  }

  const createTemporary = (workflowData?: ComfyWorkflowJSON) => {
    window.log('createTemporary')
    const workflowName = getUnconflictedWorkflowName()

    const workflow = new NordyComfyWorkflow({
      workflowName: workflowName,
      workflowId: workflowName,
      currentUserId: nordyUserStore?.nordyUser?._id ?? null,
      kind: 'temporary',
      originalCover: null,
      userId: null,
      prompt: undefined
    })

    workflow.originalContent = workflow.content = workflowData
      ? JSON.stringify(workflowData)
      : defaultGraphJSON

    workflowLookup.value[workflow.sessionWorkflowId] = workflow
    return workflow
  }

  const closeWorkflow = async (workflow: NordyComfyWorkflow) => {
    openWorkflowPaths.value = openWorkflowPaths.value.filter(
      (sessionWorkflowId) => sessionWorkflowId !== workflow.sessionWorkflowId
    )
    delete workflowLookup.value[workflow.sessionWorkflowId]
    workflow.unload()
  }

  const openedWorkflowIndexShift = (
    shift: number
  ): NordyComfyWorkflow | null => {
    const index = openWorkflowPaths.value.indexOf(
      activeWorkflow.value?.sessionWorkflowId ?? ''
    )

    if (index !== -1) {
      const length = openWorkflows.value.length
      const nextIndex = (index + shift + length) % length
      const nextWorkflow = openWorkflows.value[nextIndex]
      return nextWorkflow ?? null
    }
    return null
  }

  const persistedWorkflows = computed(() =>
    Array.from(workflows.value).filter(
      (workflow) =>
        workflow.isPersisted && !workflow.path.startsWith('subgraphs/')
    )
  )

  const syncWorkflows = async () => {
    const { hasWorkflow, parameter } = nordyApi.hasWorkflowParameter()

    const parameterWorkflowLoaded =
      hasWorkflow && parameter
        ? Boolean(
            sessionStorage.getItem(`parameterWorkflowLoaded_${parameter}`)
          )
        : false

    let parameterWorkflowSessionId: string | null = null

    // 1. URL 파라미터 워크플로우가 있고 아직 로드하지 않은 경우 → API에서 fetch
    const getParameterWorkflow =
      hasWorkflow && !parameterWorkflowLoaded
        ? await nordyApi.getWorkflow()
        : null

    if (getParameterWorkflow) {
      // image/job에서 가져온 경우 workflowName을, flow에서 가져온 경우 workflowId를 사용
      const worklfowIdByKind =
        getParameterWorkflow.kind === 'image'
          ? getParameterWorkflow.workflowName
          : getParameterWorkflow.workflowId

      // 내 워크플로우인지 확인
      const currentUserId = nordyUserStore?.nordyUser?._id
      const workflowOwnerId =
        getParameterWorkflow.workflowData?.user?._id ??
        ((getParameterWorkflow as Record<string, unknown>).userId as string) ??
        null
      const isMyWorkflow = !!currentUserId && workflowOwnerId === currentUserId

      // 내 워크플로우라면 원래 이름 그대로, 남의 것이라면 충돌방지 이름
      const displayName = isMyWorkflow
        ? getParameterWorkflow.workflowName
        : getUnconflictedWorkflowName()

      const workflow = new NordyComfyWorkflow({
        workflowId: worklfowIdByKind,
        workflowName: displayName,
        currentUserId: currentUserId ?? '',
        kind: getParameterWorkflow.kind,
        originalCover: isMyWorkflow ? getParameterWorkflow.cover : null,
        userId: workflowOwnerId,
        prompt: getParameterWorkflow.prompt
      })

      const promptStr = JSON.parse(getParameterWorkflow.prompt)

      const workflowData =
        promptStr?.workflow ?? promptStr?.workflowJson ?? defaultGraphJSON

      try {
        sessionStorage.setItem(
          workflow.sessionWorkflowId,
          JSON.stringify(workflowData)
        )
        sessionStorage.setItem(`parameterWorkflowLoaded_${parameter}`, 'true')
      } catch {
        console.warn(
          '[NordyWorkflow] sessionStorage quota exceeded during syncWorkflows'
        )
      }
      parameterWorkflowSessionId = workflow.sessionWorkflowId

      workflowLookup.value[workflow.sessionWorkflowId] = workflow
      openWorkflowPaths.value.push(workflow.sessionWorkflowId)

      // 조기 return하지 않음 — 아래에서 기존 세션 워크플로우도 함께 복원
    }

    // 2. 기존 세션 워크플로우들 복원 (파라미터 워크플로우와 중복 제외)
    const sessionStorageItems = getSessionStorageItemsByPrefix(`workflow:`)

    if (sessionStorageItems) {
      Object.entries(sessionStorageItems).forEach(([key, _]) => {
        if (parameterWorkflowSessionId === key) {
          return
        }

        const splitKey = key.split('workflow:')

        if (splitKey.length <= 1 || !splitKey[1] || splitKey[1].trim() === '') {
          return
        }

        let keyParse
        try {
          keyParse = JSON.parse(splitKey[1]) as {
            clientId: string
            kind: 'workflow' | 'image' | 'temporary'
            workflowId: string
            workflowName: string
            userId: string
            prompt: string
          }
        } catch (error) {
          console.warn(
            'Failed to parse workflow key (not a valid JSON):',
            key,
            error
          )
          return
        }

        const workflowId = keyParse.workflowId
        const workflowName = keyParse.workflowName
        const kind = keyParse.kind
        const userId = keyParse.userId

        const workflow = new NordyComfyWorkflow({
          workflowId: workflowId,
          workflowName: workflowName,
          userId: userId,
          currentUserId: nordyUserStore?.nordyUser?._id ?? '',
          kind: kind as 'workflow' | 'image' | 'temporary',
          originalCover: null,
          prompt: undefined
        })

        workflowLookup.value[workflow.sessionWorkflowId] = workflow
        openWorkflowPaths.value.push(workflow.sessionWorkflowId)
      })
    }

    // 3. URL 파라미터 워크플로우가 있으면 항상 해당 워크플로우를 활성화 대상으로 설정
    //    (리로드 시에도 파라미터 워크플로우가 우선 표시되도록 보장)
    if (hasWorkflow && parameter) {
      const targetSessionId =
        parameterWorkflowSessionId ??
        findWorkflowSessionIdByParameter(parameter)

      if (targetSessionId) {
        try {
          sessionStorage.setItem(
            `Comfy.PreviousWorkflow:${api.clientId}`,
            targetSessionId
          )
        } catch {
          // sessionStorage quota exceeded — non-critical
        }
      }
    }
  }

  /**
   * 파라미터 값(workflowId)으로 workflowLookup에서 일치하는 세션 워크플로우를 찾는다.
   */
  const findWorkflowSessionIdByParameter = (
    parameter: string
  ): string | null => {
    for (const [sessionId, workflow] of Object.entries(workflowLookup.value)) {
      if (workflow.workflowId === parameter) {
        return sessionId
      }
    }
    return null
  }

  const modifiedWorkflows = computed(() =>
    workflows.value.filter((workflow) => workflow.isModified)
  )

  const isBusy = ref<boolean>(false)

  //!nordy - API 요청 중 전역 BlockUI 스피너를 표시한다.
  // isBusy가 true이면 workspaceStore.spinner도 true로 설정하여
  // App.vue의 BlockUI가 활성화된다.
  watch(isBusy, (busy) => {
    useWorkspaceStore().spinner = busy
  })

  const saveWorkflow = async (
    workflow: NordyComfyWorkflow,
    saveName: string,
    isDuplicate: boolean = false
  ) => {
    isBusy.value = true
    try {
      const detachInfo = detachWorkflow(workflow)
      const wasActive = isActive(workflow)

      try {
        await workflow.save(saveName, isDuplicate)

        toastStore.add({
          life: 3000,
          detail: 'Successfully saved workflow'
        })
      } finally {
        attachWorkflow(workflow, detachInfo)

        if (wasActive) {
          activeWorkflow.value = workflow as NordyLoadedComfyWorkflow
        }
      }
    } finally {
      isBusy.value = false
    }
  }

  /**
   * Rename a workflow (update name on nordy-api and refresh local state)
   */
  const renameWorkflow = async (
    workflow: NordyComfyWorkflow,
    newName: string
  ) => {
    if (!workflow.isMyWorkflow) {
      throw new Error('Cannot rename a workflow that is not yours')
    }

    isBusy.value = true
    try {
      const detachInfo = detachWorkflow(workflow)
      const wasActive = isActive(workflow)

      try {
        await nordyApi.updateWorkflow(workflow.workflowId, { name: newName })
        workflow.workflowName = newName
      } finally {
        attachWorkflow(workflow, detachInfo)
        if (wasActive) {
          activeWorkflow.value = workflow as NordyLoadedComfyWorkflow
        }
      }
    } finally {
      isBusy.value = false
    }
  }

  const isSubgraphActive = ref(false)

  const activeSubgraph = shallowRef<Raw<Subgraph>>()

  //!nordy - upstream과 동일하게 동기 실행 (requestAnimationFrame 제거)
  // 서브그래프 브레드크럼 등 UI 컴포넌트가 즉시 반응해야 하므로 동기로 갱신한다.
  const updateActiveGraph = () => {
    const subgraph = app.canvas?.subgraph
    activeSubgraph.value = subgraph ? markRaw(subgraph) : undefined

    if (!app.canvas) return
    isSubgraphActive.value = isSubgraph(subgraph)
  }

  const subgraphNodeIdToSubgraph = (id: string, graph: LGraph | Subgraph) => {
    const node = graph.getNodeById(id)
    if (node?.isSubgraphNode()) return node.subgraph
  }

  const getSubgraphsFromInstanceIds = (
    currentGraph: LGraph | Subgraph,
    subgraphNodeIds: string[],
    subgraphs: Subgraph[] = []
  ): Subgraph[] => {
    const currentPart = subgraphNodeIds.shift()
    if (currentPart === undefined) return subgraphs

    const subgraph = subgraphNodeIdToSubgraph(currentPart, currentGraph)
    if (subgraph === undefined) throw new Error('Subgraph not found')

    subgraphs.push(subgraph)
    return getSubgraphsFromInstanceIds(subgraph, subgraphNodeIds, subgraphs)
  }

  const executionIdToCurrentId = (id: string) => {
    const subgraph = activeSubgraph.value

    if (!id.includes(':')) {
      return !subgraph ? id : undefined
    } else if (!subgraph) {
      return
    }

    const subgraphNodeIds = id.split(':')

    const subgraphs = getSubgraphsFromInstanceIds(
      app.rootGraph,
      subgraphNodeIds
    )
    if (subgraphs.at(-1) === subgraph) {
      return subgraphNodeIds.at(-1)
    }
  }

  watch(activeWorkflow, updateActiveGraph, { flush: 'post' })

  const saveWorkflowAs = async (
    workflow: NordyComfyWorkflow,
    saveName: string
  ) => {
    isBusy.value = true
    try {
      const activeWorkflowState = workflow.activeState
      if (!activeWorkflowState) {
        throw new Error('활성 워크플로우 상태를 가져올 수 없습니다')
      }

      const newWorkflow = new NordyComfyWorkflow({
        workflowId: saveName,
        workflowName: saveName,
        currentUserId: nordyUserStore?.nordyUser?._id ?? null,
        kind: 'temporary',
        originalCover: workflow.originalCover,
        userId: workflow.userId,
        prompt: workflow.prompt
      })

      const workflowJson = JSON.stringify(activeWorkflowState)
      newWorkflow.content = workflowJson
      newWorkflow.originalContent = workflowJson
      newWorkflow.changeTracker = markRaw(
        new ChangeTracker(
          newWorkflow as unknown as ComfyWorkflow,
          activeWorkflowState
        )
      )
      newWorkflow.lastOutputAsset = workflow.lastOutputAsset

      await newWorkflow.save(saveName, true)

      await openWorkflow(newWorkflow)

      toastStore.add({
        life: 3000,
        detail: 'Successfully saved workflow'
      })

      return newWorkflow
    } finally {
      isBusy.value = false
    }
  }

  const nodeLocatorIdToNodeExecutionId = (
    locatorId: NodeLocatorId | string,
    targetSubgraph?: Subgraph
  ): NodeExecutionId | null => {
    const parsed = parseNodeLocatorId(locatorId)
    if (!parsed) return null

    const { subgraphUuid, localNodeId } = parsed

    if (!subgraphUuid) {
      return String(localNodeId)
    }

    const findSubgraphPath = (
      graph: LGraph | Subgraph,
      targetUuid: string,
      path: NodeId[] = []
    ): NodeId[] | null => {
      if (isSubgraph(graph) && graph.id === targetUuid) {
        return path
      }

      for (const node of graph._nodes) {
        if (node.isSubgraphNode() && node.subgraph) {
          const result = findSubgraphPath(node.subgraph, targetUuid, [
            ...path,
            node.id
          ])
          if (result) return result
        }
      }

      return null
    }

    const path = findSubgraphPath(app.rootGraph, subgraphUuid)
    if (!path) return null

    if (
      targetSubgraph &&
      !path.some((_, idx) => {
        const subgraphs = getSubgraphsFromInstanceIds(
          app.rootGraph,
          path.slice(0, idx + 1).map((id) => String(id))
        )
        return subgraphs[subgraphs.length - 1] === targetSubgraph
      })
    ) {
      return null
    }

    return createNodeExecutionId([...path, localNodeId])
  }

  return {
    activeWorkflow,
    isActive,
    openWorkflows,
    openedWorkflowIndexShift,
    openWorkflow,
    openWorkflowsInBackground,
    isOpen,
    isBusy,
    closeWorkflow,
    createTemporary,
    saveWorkflow,
    saveWorkflowAs,
    renameWorkflow,
    reorderWorkflows,
    workflowLookup,
    workflows,
    persistedWorkflows,
    modifiedWorkflows,
    getWorkflowByPath,
    syncWorkflows,
    nodeIdToNodeLocatorId,
    nodeToNodeLocatorId,
    isSubgraphActive,
    activeSubgraph,
    updateActiveGraph,
    executionIdToCurrentId,
    nodeExecutionIdToNodeLocatorId,
    nodeLocatorIdToNodeId,
    nodeLocatorIdToNodeExecutionId
  }
})

/**
 * 세션스토리지에서 특정 접두사로 시작하는 모든 항목을 검색합니다
 */
function getSessionStorageItemsByPrefix(
  prefix: string
): Record<string, string> {
  const result: Record<string, string> = {}

  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i)

    if (key && key.startsWith(prefix)) {
      const value = sessionStorage.getItem(key)
      if (value !== null) {
        result[key] = value
      }
    }
  }

  return result
}
